import { Component, inject, OnInit } from '@angular/core';
import { NavbarComponent } from './components/navbar/navbar.component';
import { FooterComponent } from './components/footer/footer.component';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { RestStop } from './models/rest-stop';
import { map, Observable, shareReplay, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterOutlet } from '@angular/router';
import { RestStopService } from './services/rest-stop.service';
import { AsyncPipe, Location, NgIf } from '@angular/common';
import { BottomNavComponent } from './components/bottom-nav/bottom-nav.component';
import { RestStopDetailComponent } from './components/reststops/rest-stop-detail/rest-stop-detail.component';
import { LayoutService } from './services/layout.service';
import { NotificationPanelComponent } from './components/navbar/notification-panel/notification-panel.component';
import { MapFilterComponent } from './components/map/filter/map-filter.component';
import { Title } from '@angular/platform-browser';
import { BottomSheetService } from './services/bottom-sheet.service';
import { PeekBottomSheetComponent } from './components/peek-bottom-sheet/peek-bottom-sheet.component';
import { SeoService } from './services/seo.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { LoadingService } from './services/loading.service';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    NavbarComponent,
    FooterComponent,
    LeafletModule,
    MatSidenavContainer,
    MatSidenavContent,
    MatSidenav,
    RouterOutlet,
    AsyncPipe,
    BottomNavComponent,
    NotificationPanelComponent,
    MapFilterComponent,
    NgIf,
    PeekBottomSheetComponent,
    MatProgressBarModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  isLoading$ = inject(LoadingService).isLoading$;
  private breakpointObserver = inject(BreakpointObserver);
  private dialog = inject(MatDialog);
  private restStopService = inject(RestStopService);
  public layoutService = inject(LayoutService);
  private location = inject(Location);
  titleService = inject(Title);
  private seoService = inject(SeoService);
  private router = inject(Router);

  selectedStop?: RestStop;

  selectedRestStop$: Observable<RestStop | null>;
  isClosing$: Observable<boolean>;
  isOpening$: Observable<boolean>;

  private TITLE: string = 'Rast-WC.de – Finde schnell saubere Toiletten an Raststätten';

  constructor(private bottomSheetService: BottomSheetService) {
    this.selectedRestStop$ = this.bottomSheetService.selectedRestStop$;
    this.isClosing$ = this.bottomSheetService.isClosing$;
    this.isOpening$ = this.bottomSheetService.isOpening$;
  }

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay(),
  );

  ngOnInit(): void {
    this.initializeApp();

    this.restStopService.markerClicked$.subscribe((stop) => {
      this.selectedStop = stop;

      if (stop && stop.slug) {
        this.location.go(stop.slug);
      }

      this.isHandset$.pipe(take(1)).subscribe((isHandset) => {
        if (isHandset) {
          // Update page title and meta description when opening the Peek Bottom Sheet
          this.titleService.setTitle(`${stop.name} | ${stop.autobahn}`);
          this.seoService.updateMetaDescription(this.buildMetaDescription(stop));
          this.bottomSheetService.open(stop);
        } else {
          this.openDetailsDialog()
            .afterClosed()
            .pipe(take(1))
            .subscribe(() => {
              this.location.go('/');
              this.titleService.setTitle(this.TITLE);
              this.seoService.resetMetaDescription();
            });
        }
      });
    });
  }

  openDetailsDialog() {
    return this.dialog.open(RestStopDetailComponent, {
      data: this.selectedStop,
      width: '600px',
      maxHeight: '90vh',
    });
  }

  private buildMetaDescription(stop: RestStop): string {
    const hasReviews = !!(stop.reviewCount && stop.reviewCount > 0);
    const feePart = stop.details && stop.details.isFee ? ' (gebührenpflichtig)' : '';

    let infoPart: string;
    if (hasReviews) {
      infoPart = `${stop.averageRating.toFixed(1)}★ aus ${stop.reviewCount} Bewertungen`;
    } else {
      const details = stop.details;
      const amenityMap: { key: keyof typeof details; label: string }[] = details
        ? [
            { key: 'wifi', label: 'WLAN' },
            { key: 'playground', label: 'Spielplatz' },
            { key: 'chargingStation', label: 'Ladesäule' },
            { key: 'restaurant', label: 'Restaurant' },
            { key: 'gasStation', label: 'Tankstelle' },
            { key: 'shower', label: 'Duschen' },
            { key: 'picnicArea', label: 'Picknickbereich' },
            { key: 'kiosk', label: 'Kiosk' },
            { key: 'atm', label: 'Geldautomat' },
            { key: 'vendingMachine', label: 'Automaten' },
          ]
        : [];

      const amenities: string[] = [];
      if (details) {
        for (const entry of amenityMap) {
          const val = details[entry.key as keyof typeof details];
          if (typeof val === 'boolean' && val) amenities.push(entry.label);
        }
      }
      const top = amenities.slice(0, 3);
      if (top.length > 1) {
        const last = top.pop();
        infoPart = `Ausstattung: ${top.join(', ')} und ${last}`;
      } else if (top.length === 1) {
        infoPart = `Ausstattung: ${top[0]}`;
      } else {
        infoPart = 'Ausstattung ansehen';
      }
    }

    return `${stop.name} an der ${stop.autobahn}: Saubere WCs${feePart}, ${infoPart}. Jetzt auf Rast-WC.de prüfen.`;
  }

  onBottomSheetClosed(): void {
    // Restore URL and default title when the peek bottom sheet is closed on mobile
    this.location.go('/');
    this.titleService.setTitle(this.TITLE);
    this.seoService.resetMetaDescription();
    this.bottomSheetService.close();
  }

  onRouteActivated(): void {
    // Close any open bottom sheet when navigating to a different route; do not modify the URL here
    this.bottomSheetService.close();
  }

  isTreeRoute(): boolean {
    return this.router.url.split('?')[0] === '/tree';
  }

  initializeApp() {
    if (Capacitor.isNativePlatform()) {
      StatusBar.setOverlaysWebView({ overlay: false });

      StatusBar.setStyle({ style: Style.Dark });
      StatusBar.setBackgroundColor({ color: '#131316' });
    }

    App.addListener('backButton', () => {
      if (this.dialog.openDialogs.length > 0) {
        this.dialog.closeAll();
        return;
      }

      if (this.bottomSheetService.isBottomSheetOpen()) {
        // oder .getSelectedRestStopValue()
        this.bottomSheetService.close();
        return;
      }

      if (this.layoutService.isSidenavOpen()) {
        this.layoutService.closeSidenav();
        return;
      }

      if (this.router.url !== '/') {
        this.location.back();
        return;
      }

      App.exitApp();
    });
  }
}
